import clsx from 'clsx';
import * as CSS from 'csstype';
import {
  Children,
  type ElementType,
  forwardRef,
  type MouseEventHandler,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';

import * as styles from './Button.sty.js';
import { Label } from './Label.js';
import { Prefix } from './Prefix.js';
import { Suffix } from './Suffix.js';
import { ProgressCircle } from '../../content/progress/ProgressCircle.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import {
  type AriaDisabledProps,
  type AriaLabelingProps,
} from '../../core/types/a11y-props.js';
import { type BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type MaskingProps } from '../../core/types/masking-props.js';
import { type PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { parseBoolean } from '../../core/utils/parse-boolean.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Grid } from '../../layouts/grid/Grid.js';
import { fieldTokens } from '../../styles/field.sty.js';

const COMPONENT_NAME = 'Button';

//#region Typings

/**
 * Accepted properties for the Button.
 * @public
 */
export interface ButtonOwnProps
  extends
    AriaLabelingProps,
    AriaDisabledProps,
    WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * If a button is disabled e.g. it cannot be interacted with.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Different variants have different styles.
   * @defaultValue 'default'
   */
  variant?: 'default' | 'emphasized' | 'accent';

  /**
   * The HTML button type.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type}
   * @defaultValue 'button'
   */
  type?: 'button' | 'reset' | 'submit';

  /** Called when the button is interacted with. */
  onClick?: MouseEventHandler;

  /**
   * The width of the button.
   * @defaultValue 'content'
   */
  width?: 'content' | 'full' | CSS.Property.Width<string & {}>;

  /**
   * The color of the button. This should be chosen based on the context
   * the button is used in.
   * @defaultValue 'neutral'
   */
  color?: 'primary' | 'neutral' | 'success' | 'critical' | 'warning';

  /**
   * Controls the text alignment inside the button. Only affects the button
   * if the width is not set to 'content'.
   * @defaultValue 'center'
   */
  textAlign?: 'center' | 'start';

  /**
   * The size of the button.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';

  /**
   * The current loading state of the button. If true, a loading icon is shown, the button stays in the tab order with aria-disabled=true,
   * and its busy state is signalled via aria-busy=true.
   * @defaultValue false
   */
  loading?: boolean;
}

/**
 * Merge own props with others inherited from the underlying element type.
 * @public
 */
export type ButtonProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  ButtonOwnProps
>;

type ButtonSlots = {
  label?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  freeform?: ReactNode[];
};

//#endregion

/**
 * Iterates over child nodes to extract specific component slots.
 * @public
 */
export default function getButtonSlots(children: ReactNode) {
  const slots: ButtonSlots = {};

  Children.forEach(children, (child) => {
    if (isSlot(child, Suffix)) {
      slots.suffix = child;
    } else if (isSlot(child, Prefix)) {
      slots.prefix = child;
    } else if (isSlot(child, Label)) {
      slots.label = child;
    } else {
      if (child !== null && child !== undefined) {
        slots.freeform ??= [];
        slots.freeform.push(child);
      }
    }
  });

  return slots;
}

const _Button: <E extends ElementType = 'button'>(
  props: ButtonProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(props: ButtonProps<E>, ref: typeof props.ref) => {
    const {
      children,
      variant = 'default',
      disabled,
      'aria-disabled': ariaDisabledProp,
      required,
      id: propId,
      type = 'button',
      onClick,
      ariaLabel,
      width = 'content',
      color = 'neutral',
      textAlign = 'center',
      size = 'default',
      loading = false,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      as,
      ...remainingProps
    }: ButtonProps<E> = props;
    const slots = getButtonSlots(children);

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const ariaDisabled = parseBoolean(ariaDisabledProp);

    const nativeDisabledOrLoading = useMemo(
      () => (loading ? true : disabled),
      [disabled, loading],
    );
    const disabledOrLoading = useMemo(
      () => nativeDisabledOrLoading || ariaDisabled,
      [ariaDisabled, nativeDisabledOrLoading],
    );

    const ariaLabelingProps = useAriaLabelingProps(restProps);

    const handleButtonClick: MouseEventHandler<HTMLElement> = (event) => {
      if (nativeDisabledOrLoading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onClick?.(event);
    };

    const buttonProps = {
      ref,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      required,
      disabled: disabled && !ariaDisabled,
      id: propId,
      type,
      ...ariaLabelingProps,
      'aria-disabled': loading ? true : ariaDisabledProp,
      'aria-busy': loading,
      onClick: handleButtonClick,
    };

    // Propagation needs to be disabled on the disabled button because
    // it would still trigger actions on parents that would listen to it.
    // For example the menu or the useOverlay hook.
    const disabledPropagationProps = {
      onPointerDown(evt: PointerEvent) {
        if (nativeDisabledOrLoading) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      },
      onPointerUp(evt: PointerEvent) {
        if (nativeDisabledOrLoading) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      },
    };

    const hasChildren = children || children === 0;

    const buttonMergedProps = {
      ...mergeProps(
        buttonProps,
        disabledPropagationProps,
        // Need to cast this one as the inference of mergeProps would generate
        // a not allowed overlap. The restProps type is too complex due to the
        // polymorphic inheritance.
        restProps as Record<string, unknown>,
        behavioralTrackingProps,
      ),
    };

    const intl = useIntl();

    // Make the component polymorphic
    const Component = as || 'button';

    // Render a void element if no children are set and it's not loading (so self-closing polymorphed tags won't break)
    if (!hasChildren) {
      return (
        <Component
          data-dt-component={COMPONENT_NAME}
          {...buttonMergedProps}
          className={clsx(
            consumerClassName,
            'strato-inputgroup',
            fieldTokens({ variant, color, disabled: disabledOrLoading }),
            styles.button({
              disabled: disabledOrLoading,
              textAlign,
              size,
            }),
            focusRingStatic({
              when: 'focus-visible',
              color,
              disabled,
            }),
            {
              [`${styles.widths.content}`]: width === 'content',
              [`${styles.widths.full}`]: width === 'full',
            },
          )}
          style={{
            width: width !== 'content' && width !== 'full' ? width : undefined,
          }}
        />
      );
    }

    return (
      <Component
        data-dt-component={COMPONENT_NAME}
        className={clsx(
          consumerClassName,
          'strato-inputgroup',
          fieldTokens({
            variant,
            color,
            disabled: disabledOrLoading || ariaDisabled,
          }),
          styles.button({
            disabled: disabledOrLoading || ariaDisabled,
            textAlign,
            size,
          }),
          {
            [`${styles.widths.content}`]: width === 'content',
            [`${styles.widths.full}`]: width === 'full',
          },
          focusRingStatic({
            when: 'focus-visible',
            color,
            disabled,
          }),
        )}
        style={{
          width: width !== 'content' && width !== 'full' ? width : undefined,
          ...consumerStyle,
        }}
        {...buttonMergedProps}
      >
        {loading && (
          <div className={styles.loading}>
            <ProgressCircle
              size="small"
              color={color}
              aria-label={intl.formatMessage({
                defaultMessage: 'Loading',
                id: 'cycz+bYG+VH7Of4e',
                description: 'Label for the loading state.',
              })}
            />
          </div>
        )}
        <Grid
          gap={size === 'default' ? 4 : 2}
          grid="auto / auto-flow auto"
          width="100%"
          maxWidth="100%"
          alignItems="center"
          justifyContent={textAlign === 'start' ? 'space-between' : 'center'}
          style={{
            visibility: loading ? 'hidden' : undefined,
          }}
        >
          {textAlign === 'start' ? (
            <Flex minWidth={0} gap={size === 'default' ? 4 : 2}>
              {slots.prefix}
              {slots.label}
              {slots.freeform && <Button.Label>{slots.freeform}</Button.Label>}
            </Flex>
          ) : (
            <>
              {slots.prefix}
              {slots.label}
              {slots.freeform && <Button.Label>{slots.freeform}</Button.Label>}
            </>
          )}
          {slots.suffix}
        </Grid>
      </Component>
    );
  },
);

(_Button as typeof _Button & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * Buttons let users trigger actions or events
 * with a single click, or by pressing
 * `Enter` or `Space` while the button has focus.
 * @public
 */
export const Button = Object.assign(_Button, {
  Suffix,
  Prefix,
  Label,
});
